/* eslint-disable semi */
import axios from 'axios';

const debug = process.env.NODE_ENV === 'development';

// オブジェクト識別No
let oid = 0;

/**
 * axiosベースのAPIクライアント
 *
 * 通信をキャンセルするためのcancelメソッド、および、通信をセグメント管理する
 * ためのsegmentメソッドを追加しています。
 */
export default class ApiClient {
  constructor (config = {}) {
    this.oid = oid++;
    this.config = config;
    this.beforeHooks = [];
    this.afterHooks = [];
    this.interceptors = [];
    this.cancelTokens = {};
    this.sequence = 0;
    this.segments = {};
    this.axios = axios.create(this.config);
  }

  /**
   * 通信結果が通信キャンセルによるものか判定します
   * @param {*} reason
   */
  isCanceled (reason) {
    return axios.isCancel(reason);
  }

  /**
   * 通信の事前処理フックを追加する
   *
   * NOTE
   * 事前処理フックについては、下記のインターフェースを備える必要があります。
   * function(config){return config}
   * see axios.interceptors.request
   *
   * @param {*} hook
   */
  addBeforeHook (hook) {
    this.beforeHooks.push(hook);
    this.interceptors.push(this.axios.interceptors.request.use(hook));
  }

  /**
   * 通信結果のPromiseを処理する事後処理フックを追加する
   *
   * NOTE
   * 事後処理フックについては、下記のインターフェースである必要があります。
   * function(promise) { return promise 又は throw reason }
   *
   * @param {*} hook
   */
  addAfterHook (hook) {
    this.afterHooks.push(hook);
  }

  /**
   * 下層セグメントのAPIクライアントを取得する
   * @param {*} segment
   */
  segment (segment) {
    if (!(segment in this.segments)) {
      // 下層セグメントは自身と同一のconfigとhookを継承します
      const api = new ApiClient(this.config);
      this.beforeHooks.forEach(hook => api.addBeforeHook(hook));
      this.afterHooks.forEach(hook => api.addAfterHook(hook));
      this.segments[segment] = api;
    }
    return this.segments[segment];
  }

  /**
   * get による通信を行う
   * @param {*} url
   * @param {*} config
   */
  get (url, config = {}) {
    let margedConfig = Object.assign({}, config);
    margedConfig = Object.assign(margedConfig, {
      url: url,
      method: 'get'
    });
    return this.request(margedConfig);
  }

  /**
   * post による通信を行う
   * @param {*} url
   * @param {*} data
   * @param {*} config
   */
  post (url, data = {}, config = {}) {
    let margedConfig = Object.assign({}, config);
    margedConfig = Object.assign(margedConfig, {
      url: url,
      method: 'post',
      data: data
    });
    return this.request(margedConfig);
  }

  /**
   * head による通信を行う
   * @param {*} url
   * @param {*} config
   */
  head (url, config = {}) {
    let margedConfig = Object.assign({}, config);
    margedConfig = Object.assign(margedConfig, {
      url: url,
      method: 'head'
    });
    return this.request(margedConfig);
  }

  /**
   * options による通信を行う
   * @param {*} url
   * @param {*} config
   */
  options (url, config = {}) {
    let margedConfig = Object.assign({}, config);
    margedConfig = Object.assign(margedConfig, {
      url: url,
      method: 'options'
    });
    return this.request(margedConfig);
  }

  /**
   * delete による通信を行う
   * @param {*} url
   * @param {*} config
   */
  delete (url, config = {}) {
    let margedConfig = Object.assign({}, config);
    margedConfig = Object.assign(margedConfig, {
      url: url,
      method: 'delete'
    });
    return this.request(margedConfig);
  }

  /**
   * put による通信を行う
   * @param {*} url
   * @param {*} data
   * @param {*} config
   */
  put (url, data = {}, config = {}) {
    let margedConfig = Object.assign({}, config);
    margedConfig = Object.assign(margedConfig, {
      url: url,
      method: 'put',
      data: data
    });
    return this.request(margedConfig);
  }

  /**
   * patch による通信を行う
   * @param {*} url
   * @param {*} data
   * @param {*} config
   */
  patch (url, data = {}, config = {}) {
    let margedConfig = Object.assign({}, config);
    margedConfig = Object.assign(margedConfig, {
      url: url,
      method: 'patch',
      data: data
    });
    return this.request(margedConfig);
  }

  /**
   * 通信を行う
   * @param {*} config
   */
  request (config = {}) {
    let margedConfig = Object.assign({}, config);

    // 通信シーケンス番号をキーにキャンセル用トークンを格納する
    // このキャンセル用トークンは通信終了時に破棄される
    const sequence = this.sequence++;
    margedConfig.cancelToken = new axios.CancelToken(token => {
      this.cancelTokens[sequence] = token;
    });

    // 通信処理を行い呼び出し元にPromiseを返却する
    let result = this.axios
      .request(margedConfig)
      .then(response => {
        if (this.cancelTokens) {
          delete this.cancelTokens[sequence];
        }
        return response;
      })
      .catch(reason => {
        if (this.cancelTokens) {
          delete this.cancelTokens[sequence];
        }
        throw reason;
      });

    // ハンドラーを実行
    this.afterHooks.forEach(hook => {
      result = hook(result);
    });

    return result;
  }

  /**
   * 未完了の通信をキャンセルする
   */
  cancel () {
    Object.keys(this.cancelTokens).forEach(sequence => {
      this.cancelTokens[sequence]();
      delete this.cancelTokens[sequence];
    });

    // 下層のセグメントもキャンセル
    Object.keys(this.segments).forEach(segment => {
      this.segments[segment].cancel();
    });

    return this;
  }

  /**
   * オブジェクトを破棄する
   */
  destroy () {
    if (debug) {
      console.debug(`ApiClient: ${this.oid} destroy.`);
    }

    // キャンセル可能な通信をすべてキャンセル
    this.cancel();

    // 下層のセグメントも破棄
    Object.keys(this.segments).forEach(segment => {
      this.segments[segment].destroy();
      delete this.segments[segment];
    });

    // hookを破棄
    this.interceptors.forEach(interceptor => {
      this.axios.interceptors.request.eject(interceptor);
    })

    delete this.config;
    delete this.axios;
    delete this.cancelTokens;
    delete this.segments;
    delete this.sequence;
    delete this.beforeHooks;
    delete this.afterHooks;
    delete this.interceptors;
  }
}



// WEBPACK FOOTER //
// ./src/core/libs/ApiClient.js